import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, Crop, Check, X } from 'lucide-react';
import { playTick } from '../utils/synthesizer';

interface ImageCropperProps {
  imageSrc: string; // original raw base64 or file URL
  onCropComplete: (croppedBase64: string) => void;
  onCancel: () => void;
}

export const ImageCropper: React.FC<ImageCropperProps> = ({ imageSrc, onCropComplete, onCancel }) => {
  const [rotation, setRotation] = useState<number>(0); // degrees: 0, 90, 180, 270
  const imageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Generate cropped preview when source or rotation changes
  useEffect(() => {
    generatePreview();
  }, [rotation, imageSrc]);

  const generatePreview = () => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Determine dimensions based on rotation
      const is90or270 = rotation === 90 || rotation === 270;
      const srcWidth = img.naturalWidth;
      const srcHeight = img.naturalHeight;
      
      const width = is90or270 ? srcHeight : srcWidth;
      const height = is90or270 ? srcWidth : srcHeight;

      canvas.width = width;
      canvas.height = height;

      // Handle translation & rotation around center
      ctx.translate(width / 2, height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -srcWidth / 2, -srcHeight / 2);

      // Now, dynamic center crop to exactly 5:7 Aspect Ratio
      const targetRatio = 5 / 7;
      let finalWidth = width;
      let finalHeight = width / targetRatio;

      if (finalHeight > height) {
        finalHeight = height;
        finalWidth = height * targetRatio;
      }

      const startX = (width - finalWidth) / 2;
      const startY = (height - finalHeight) / 2;

      // Extract cropped area to output 500x700 image for optimal lightweight performance and crisp text rendering
      const cropCanvas = canvasRef.current || document.createElement('canvas');
      cropCanvas.width = 500;
      cropCanvas.height = 700;
      const cropCtx = cropCanvas.getContext('2d');
      if (cropCtx) {
        cropCtx.drawImage(
          canvas,
          startX, startY, finalWidth, finalHeight, // source coordinate
          0, 0, 500, 700                           // target coordinate
        );
        const dataUrl = cropCanvas.toDataURL('image/jpeg', 0.85);
        setPreviewUrl(dataUrl);
      }
    };
  };

  const handleRotate = () => {
    playTick();
    setRotation(r => (r + 90) % 360);
  };

  const handleSave = () => {
    playTick();
    if (previewUrl) {
      onCropComplete(previewUrl);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/95 z-50 flex flex-col items-center justify-center p-8 select-none" style={{ backgroundColor: '#0047abf2' }}>
      <div className="max-w-2xl w-full rounded-3xl border shadow-2xl p-8 flex flex-col items-center" style={{ backgroundColor: '#4b006e', borderColor: '#0047ab' }}>
        
        {/* Header */}
        <div className="w-full text-center mb-6">
          <h3 className="text-3xl font-cinzel text-amber-500 font-bold mb-1">CROP &amp; ROTATE</h3>
          <p className="text-lg text-slate-200" style={{ color: '#f7f9fc' }}>Puzzles are automatically formatted to a 5:7 Portrait Jigsaw grid.</p>
        </div>

        {/* Hidden internal canvas */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Big Preview Area */}
        <div className="relative border-4 border-amber-500/20 rounded-2xl overflow-hidden bg-slate-950 shadow-inner p-2 mb-8 flex justify-center items-center" style={{ width: '320px', height: '448px' }}>
          {previewUrl ? (
            <img src={previewUrl} alt="Cropping preview" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <span className="text-slate-500 animate-pulse text-lg">Preparing Image...</span>
          )}
          
          <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur text-amber-500 px-4 py-2 rounded-full font-bold text-sm tracking-wide border border-amber-500/30">
            Ratio 5:7
          </div>
        </div>

        {/* Massive Touch-Friendly Control Bar */}
        <div className="grid grid-cols-3 gap-6 w-full max-w-lg">
          <button
            onClick={onCancel}
            className="flex flex-col items-center justify-center gap-2 py-5 rounded-2xl border transition"
            style={{ minHeight: '90px', backgroundColor: '#f4eeee', color: '#0047ab', borderColor: '#f87171' }}
          >
            <X className="w-8 h-8 text-rose-600" />
            <span className="text-sm font-semibold">Cancel</span>
          </button>

          <button
            onClick={handleRotate}
            className="flex flex-col items-center justify-center gap-2 py-5 rounded-2xl border transition"
            style={{ minHeight: '90px', backgroundColor: '#f7f9fb', color: '#0047ab', borderColor: '#0047ab' }}
          >
            <RotateCw className="w-8 h-8 text-indigo-600" />
            <span className="text-sm font-semibold">Rotate +90°</span>
          </button>

          <button
            onClick={handleSave}
            disabled={!previewUrl}
            className="flex flex-col items-center justify-center gap-2 py-5 rounded-2xl border font-bold transition disabled:opacity-50"
            style={{ minHeight: '90px', backgroundColor: '#fcfdfe', color: '#0047ab', borderColor: '#0047ab' }}
          >
            <Check className="w-8 h-8 text-emerald-600" />
            <span className="text-sm font-bold">Confirm Crop</span>
          </button>
        </div>

      </div>
    </div>
  );
};
